import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Image as ImageIcon, Upload, Download, Trash2, X, Tag, Crown } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Same tag config as WhiteboardPage — keep the two in sync visually.
const PLAY_TAGS = [
  { value: 'offense', label: 'Offense', chip: 'bg-orange-500 text-white', dot: 'bg-orange-500' },
  { value: 'defense', label: 'Defense', chip: 'bg-blue-600 text-white', dot: 'bg-blue-600' },
  { value: 'inbounds', label: 'Inbounds', chip: 'bg-purple-600 text-white', dot: 'bg-purple-600' },
  { value: 'press', label: 'Press', chip: 'bg-red-600 text-white', dot: 'bg-red-600' },
  { value: 'other', label: 'Other', chip: 'bg-gray-500 text-white', dot: 'bg-gray-500' },
];
const getTagMeta = (tag) => PLAY_TAGS.find((t) => t.value === tag) || null;

export default function PhotosPage({ user, onLogout }) {
  const [photos, setPhotos] = useState([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [showBulkTagDialog, setShowBulkTagDialog] = useState(false);
  const [bulkTagValue, setBulkTagValue] = useState('offense');
  const [isBulkTagging, setIsBulkTagging] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadData, setUploadData] = useState({
    category: 'team',
    description: ''
  });
  const [viewPhoto, setViewPhoto] = useState(null);
  const [tagPhoto, setTagPhoto] = useState(null);
  const [tagValue, setTagValue] = useState('offense');

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.heic']
    },
    multiple: true,
    onDrop: (acceptedFiles) => {
      setUploadFiles(acceptedFiles);
    }
  });

  useEffect(() => {
    loadPhotos();
  }, []);

  const loadPhotos = async () => {
    try {
      const response = await axios.get(`${API}/photos?team_id=${user.team_id}`);
      setPhotos(response.data);
    } catch (error) {
      toast.error('Failed to load photos');
    }
  };

  const handleUploadPhotos = async () => {
    if (uploadFiles.length === 0) {
      toast.error('Please select photos to upload');
      return;
    }

    const formData = new FormData();
    uploadFiles.forEach((file) => {
      formData.append('files', file);
    });
    formData.append('team_id', user.team_id);
    formData.append('category', uploadData.category);
    formData.append('description', uploadData.description);
    formData.append('uploaded_by', user.id);

    try {
      await axios.post(`${API}/photos/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${uploadFiles.length} photo(s) uploaded successfully!`);
      setShowUploadDialog(false);
      setUploadFiles([]);
      setUploadData({ category: 'team', description: '' });
      loadPhotos();
    } catch (error) {
      toast.error('Failed to upload photos');
    }
  };

  const togglePhotoSelection = (photoId) => {
    setSelectedPhotos(prev => 
      prev.includes(photoId) 
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    );
  };

  const handleExportSelected = async () => {
    if (selectedPhotos.length === 0) {
      toast.error('Please select photos to export');
      return;
    }

    try {
      const response = await axios.post(`${API}/photos/export`, 
        { photo_ids: selectedPhotos },
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `photos_${Date.now()}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${selectedPhotos.length} photo(s) exported successfully!`);
      setSelectedPhotos([]);
    } catch (error) {
      toast.error('Failed to export photos');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;

    try {
      await axios.delete(`${API}/photos/${photoId}`);
      toast.success('Photo deleted successfully!');
      loadPhotos();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const handleSelectAll = () => {
    if (selectedPhotos.length === photos.length) {
      setSelectedPhotos([]);
    } else {
      setSelectedPhotos(photos.map(p => p.id));
    }
  };

  const handleBulkAssignTag = async () => {
    if (selectedPhotos.length === 0) return;
    setIsBulkTagging(true);
    try {
      await Promise.all(
        selectedPhotos.map((id) => axios.put(`${API}/photos/${id}`, { tag: bulkTagValue }))
      );
      toast.success(`${selectedPhotos.length} photo(s) tagged as ${getTagMeta(bulkTagValue)?.label}`);
      setShowBulkTagDialog(false);
      setSelectedPhotos([]);
      loadPhotos();
    } catch (err) {
      toast.error('Failed to bulk-tag some photos');
    } finally {
      setIsBulkTagging(false);
    }
  };

  // Bulk Cover From Grid — set/clear cover directly from Photos page.
  const toggleCoverPhoto = async (photo, e) => {
    e.stopPropagation();
    const next = !photo.is_cover;
    try {
      await axios.put(`${API}/photos/${photo.id}`, { is_cover: next });
      toast.success(next ? 'Set as cover play' : 'Cleared cover');
      loadPhotos();
    } catch (err) {
      toast.error('Failed to update cover');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8 space-y-8" data-testid="photos-page">
        <div className="flex items-center justify-between border-b border-border pb-6">
          <div>
            <h1 className="font-heading text-5xl font-black uppercase tracking-tighter text-primary">
              Team Photos
            </h1>
            <p className="text-muted-foreground mt-2">Upload and manage team photos and memories</p>
          </div>
          <div className="flex gap-2">
            {selectedPhotos.length > 0 && (
              <Button variant="outline" onClick={handleExportSelected} data-testid="export-photos-btn">
                <Download className="h-4 w-4 mr-2" />
                EXPORT ({selectedPhotos.length})
              </Button>
            )}
            {user.role !== 'player' && (
              <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="upload-photos-btn">
                    <Upload className="h-4 w-4 mr-2" />
                    UPLOAD PHOTOS
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl uppercase">Upload Photos</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div {...getRootProps()} className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                      isDragActive ? 'border-primary bg-primary/10' : 'border-muted-foreground/20 hover:border-primary/50'
                    }`} data-testid="photo-dropzone">
                      <input {...getInputProps()} />
                      <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      {uploadFiles.length > 0 ? (
                        <div>
                          <p className="text-sm font-semibold mb-2">{uploadFiles.length} photo(s) selected</p>
                          <div className="flex flex-wrap gap-2 justify-center">
                            {uploadFiles.map((file, idx) => (
                              <div key={idx} className="text-xs bg-muted px-2 py-1 rounded">
                                {file.name}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm font-semibold">Drop photos here or click to browse</p>
                          <p className="text-xs text-muted-foreground mt-1">Supports JPG, PNG, HEIC, WebP</p>
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Select
                        value={uploadData.category}
                        onValueChange={(value) => setUploadData({...uploadData, category: value})}
                      >
                        <SelectTrigger data-testid="photo-category">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="team">Team Photos</SelectItem>
                          <SelectItem value="game">Game Day</SelectItem>
                          <SelectItem value="practice">Practice</SelectItem>
                          <SelectItem value="event">Special Event</SelectItem>
                          <SelectItem value="player">Player Photos</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Description (Optional)</Label>
                      <Input
                        data-testid="photo-description"
                        value={uploadData.description}
                        onChange={(e) => setUploadData({...uploadData, description: e.target.value})}
                        placeholder="Add a description..."
                      />
                    </div>
                    <Button onClick={handleUploadPhotos} className="w-full" data-testid="submit-photos-btn">
                      <Upload className="h-4 w-4 mr-2" />
                      UPLOAD {uploadFiles.length > 0 && `(${uploadFiles.length})`}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Selection Controls */}
        {photos.length > 0 && (
          <div className="flex items-center justify-between bg-muted/30 p-4 rounded-lg">
            <div className="flex items-center gap-4 flex-wrap">
              <Button variant="outline" size="sm" onClick={handleSelectAll} data-testid="select-all-btn">
                {selectedPhotos.length === photos.length ? 'Deselect All' : 'Select All'}
              </Button>
              {selectedPhotos.length > 0 && (
                <>
                  <span className="text-sm font-medium">
                    {selectedPhotos.length} photo(s) selected
                  </span>
                  <Button
                    size="sm"
                    onClick={() => { setBulkTagValue('offense'); setShowBulkTagDialog(true); }}
                    data-testid="bulk-assign-tag-btn"
                  >
                    <Tag className="h-4 w-4 mr-2" />
                    Assign Tag ({selectedPhotos.length})
                  </Button>
                </>
              )}
            </div>
            <div className="text-sm text-muted-foreground">
              Total: {photos.length} photo(s)
            </div>
          </div>
        )}

        {/* Photos Grid */}
        {photos.length === 0 ? (
          <Card className="border-2">
            <CardContent className="p-12 text-center">
              <ImageIcon className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
              <h3 className="font-heading text-2xl mt-4">No Photos Yet</h3>
              <p className="text-muted-foreground mt-2">Upload your first team photo to get started</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {photos.map((photo) => (
              <Card 
                key={photo.id} 
                className={`border-2 overflow-hidden transition-all cursor-pointer ${
                  selectedPhotos.includes(photo.id) 
                    ? 'border-primary ring-4 ring-primary/20' 
                    : 'hover:border-primary/50'
                }`}
                data-testid={`photo-card-${photo.id}`}
              >
                <div className="relative aspect-square bg-muted">
                  <img 
                    src={`${BACKEND_URL}${photo.url}`}
                    alt={photo.description || 'Team photo'}
                    className="absolute inset-0 w-full h-full object-cover"
                    onClick={() => setViewPhoto(photo)}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-full"><span class="text-muted-foreground">Image unavailable</span></div>';
                    }}
                  />
                  {photo.category === 'whiteboard' && (
                    <Button
                      size="icon"
                      variant="secondary"
                      className={`absolute bottom-2 right-2 h-8 w-8 shadow ${photo.is_cover ? 'bg-amber-400 hover:bg-amber-500 text-black' : 'bg-white/80 hover:bg-white text-muted-foreground hover:text-amber-500'}`}
                      onClick={(e) => toggleCoverPhoto(photo, e)}
                      data-testid={`grid-cover-btn-${photo.id}`}
                      title={photo.is_cover ? 'Cover play — click to clear' : 'Set as cover play'}
                    >
                      <Crown className="h-4 w-4" />
                    </Button>
                  )}
                  {photo.category === 'whiteboard' && (
                    (() => {
                      const meta = getTagMeta(photo.tag);
                      return meta ? (
                        <span
                          className={`absolute bottom-2 left-2 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${meta.chip} shadow`}
                          data-testid={`photo-tag-${photo.id}`}
                        >
                          {meta.label}
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          variant="secondary"
                          className="absolute bottom-2 left-2 h-7 text-[10px] font-bold uppercase shadow"
                          onClick={(e) => {
                            e.stopPropagation();
                            setTagValue('offense');
                            setTagPhoto(photo);
                          }}
                          data-testid={`categorize-photo-${photo.id}`}
                        >
                          <Tag className="h-3 w-3 mr-1" />
                          Categorize
                        </Button>
                      );
                    })()
                  )}
                  <div className="absolute top-2 left-2">
                    <input
                      type="checkbox"
                      checked={selectedPhotos.includes(photo.id)}
                      onChange={() => togglePhotoSelection(photo.id)}
                      className="h-5 w-5 cursor-pointer"
                      data-testid={`photo-checkbox-${photo.id}`}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  {user.role !== 'player' && (
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePhoto(photo.id);
                      }}
                      data-testid={`delete-photo-${photo.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <CardContent className="p-3">
                  <div className="text-xs text-muted-foreground mb-1 capitalize">
                    {photo.category}
                  </div>
                  {photo.description && (
                    <p className="text-sm truncate" title={photo.description}>
                      {photo.description}
                    </p>
                  )}
                  <div className="text-xs text-muted-foreground mt-1">
                    {format(new Date(photo.created_at), 'MMM d, yyyy')}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Photo Viewer Dialog */}
        {viewPhoto && (
          <Dialog open={!!viewPhoto} onOpenChange={() => setViewPhoto(null)}>
            <DialogContent className="max-w-4xl">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl uppercase">
                  {viewPhoto.description || 'Team Photo'}
                </DialogTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-4 right-4"
                  onClick={() => setViewPhoto(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </DialogHeader>
              <div className="space-y-4">
                <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                  <img
                    src={`${BACKEND_URL}${viewPhoto.url}`}
                    alt={viewPhoto.description}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div>
                    <span className="font-semibold capitalize">{viewPhoto.category}</span>
                    <span className="text-muted-foreground ml-2">
                      {format(new Date(viewPhoto.created_at), 'MMMM d, yyyy')}
                    </span>
                  </div>
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Categorize Dialog */}
        <Dialog open={!!tagPhoto} onOpenChange={(v) => { if (!v) setTagPhoto(null); }}>
          <DialogContent className="max-w-md" data-testid="categorize-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                <Tag className="h-5 w-5" />
                Categorize Play
              </DialogTitle>
            </DialogHeader>
            {tagPhoto && (
              <div className="space-y-3">
                <img src={`${BACKEND_URL}${tagPhoto.url}`} alt="" className="w-full h-40 object-cover rounded" />
                <div>
                  <Label>Category</Label>
                  <Select value={tagValue} onValueChange={setTagValue}>
                    <SelectTrigger data-testid="categorize-tag-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PLAY_TAGS.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          <span className="inline-flex items-center gap-2">
                            <span className={`inline-block w-3 h-3 rounded-full ${t.dot}`} />
                            {t.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="ghost" onClick={() => setTagPhoto(null)}>Cancel</Button>
                  <Button
                    onClick={async () => {
                      try {
                        await axios.put(`${API}/photos/${tagPhoto.id}`, { tag: tagValue });
                        toast.success(`Tagged as ${getTagMeta(tagValue)?.label}`);
                        setTagPhoto(null);
                        loadPhotos();
                      } catch (err) {
                        toast.error('Failed to save tag');
                      }
                    }}
                    data-testid="save-categorize-btn"
                  >
                    Save
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Bulk Tag Dialog */}
        <Dialog open={showBulkTagDialog} onOpenChange={setShowBulkTagDialog}>
          <DialogContent className="max-w-md" data-testid="bulk-tag-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                <Tag className="h-5 w-5" />
                Assign Tag to {selectedPhotos.length} Photo{selectedPhotos.length === 1 ? '' : 's'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                All selected photos will be updated with the tag below. Useful for labeling 20+ old plays in one click.
              </p>
              <div>
                <Label>Tag</Label>
                <Select value={bulkTagValue} onValueChange={setBulkTagValue}>
                  <SelectTrigger data-testid="bulk-tag-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLAY_TAGS.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        <span className="inline-flex items-center gap-2">
                          <span className={`inline-block w-3 h-3 rounded-full ${t.dot}`} />
                          {t.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setShowBulkTagDialog(false)}>Cancel</Button>
                <Button
                  onClick={handleBulkAssignTag}
                  disabled={isBulkTagging}
                  data-testid="submit-bulk-tag-btn"
                >
                  {isBulkTagging ? 'Applying…' : `Apply to ${selectedPhotos.length}`}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}